import { Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import {
  ShieldCheck,
  LifeBuoy,
  Instagram,
  Facebook,
  Youtube,
  Mail,
  Heart,
  Info,
  UserCircle2,
  HeartHandshake,
  HelpCircle,
  Lock,
  FileText,
  ShieldAlert,
  BookOpen,
  Cookie,
  Plus,
  type LucideIcon,
} from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import logoAsset from "@/assets/logo.png.asset.json";
import { openCookiePreferences } from "@/lib/cookie-consent";
import { openExternal } from "@/lib/open-external";
import { readStoredAuthSession } from "@/lib/auth-session";
const logo = logoAsset.url;

function TikTokIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" fill="currentColor" className={className}>
      <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5.8 20.1a6.34 6.34 0 0 0 10.86-4.43V8.65a8.16 8.16 0 0 0 4.77 1.52V6.71a4.79 4.79 0 0 1-1.84-.02Z" />
    </svg>
  );
}

function PinterestIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden>
      <path d="M12 2C6.48 2 2 6.48 2 12c0 4.24 2.64 7.86 6.36 9.31-.09-.79-.17-2 .04-2.86.19-.78 1.24-4.95 1.24-4.95s-.32-.63-.32-1.57c0-1.47.85-2.57 1.91-2.57.9 0 1.34.68 1.34 1.49 0 .91-.58 2.27-.88 3.53-.25 1.06.53 1.92 1.57 1.92 1.89 0 3.34-1.99 3.34-4.86 0-2.54-1.83-4.32-4.44-4.32-3.03 0-4.81 2.27-4.81 4.61 0 .91.35 1.89.79 2.42.09.1.1.19.07.3-.08.32-.26 1.06-.29 1.21-.05.2-.16.24-.36.14-1.36-.63-2.21-2.62-2.21-4.21 0-3.43 2.49-6.58 7.18-6.58 3.77 0 6.7 2.69 6.7 6.28 0 3.75-2.36 6.76-5.64 6.76-1.1 0-2.14-.57-2.49-1.25l-.68 2.59c-.25.95-.91 2.14-1.36 2.86.99.31 2.05.48 3.15.48 5.52 0 10-4.48 10-10S17.52 2 12 2z" />
    </svg>
  );
}

const SOCIALS = [
  { name: "Instagram", href: "https://www.instagram.com/recoverytrauma_", Icon: Instagram },
  { name: "Facebook", href: "https://www.facebook.com/people/Recovery-Trauma/61568168060890", Icon: Facebook },
  { name: "Pinterest", href: "https://www.pinterest.com/recoverytrauma", Icon: PinterestIcon },
  { name: "TikTok", href: "https://www.tiktok.com/@recoverytrauma", Icon: TikTokIcon },
  { name: "YouTube", href: "https://www.youtube.com/@RecoveryTrauma", Icon: Youtube },
];

type FooterLink = {
  to?: string;
  href?: string;
  onClick?: () => void;
  label: string;
  Icon: LucideIcon;
  emphasis?: boolean;
};

type FooterSection = {
  key: string;
  title: string;
  Icon: LucideIcon;
  links: FooterLink[];
};

// Simplified footer — About / Support / Legal / Contact.
// No support email exposed directly; "Contact Support" routes to /support.
const SECTIONS: FooterSection[] = [
  {
    key: "about",
    title: "About",
    Icon: Info,
    links: [
      { to: "/about", label: "About Recovery Trauma™", Icon: HeartHandshake },
      { to: "/about", label: "Meet the Founder", Icon: UserCircle2 },
      { to: "/escalation-pathway", label: "Escalation Pathway", Icon: ShieldAlert },
    ],
  },
  {
    key: "support",
    title: "Support",
    Icon: LifeBuoy,
    links: [
      { to: "/crisis", label: "Crisis Support", Icon: ShieldAlert, emphasis: true },
      { to: "/faq", label: "FAQ", Icon: HelpCircle },
      { to: "/community-guidelines", label: "Community Guidelines", Icon: BookOpen },
    ],
  },
  {
    key: "legal",
    title: "Legal",
    Icon: FileText,
    links: [
      { to: "/privacy", label: "Privacy Policy", Icon: Lock },
      { to: "/terms", label: "Terms & Conditions", Icon: FileText },
      { to: "/disclaimer", label: "Disclaimer", Icon: ShieldAlert },
      { to: "/ai-disclaimer", label: "AI Disclaimer", Icon: Info },
    ],
  },
  {
    key: "contact",
    title: "Contact",
    Icon: Mail,
    links: [
      { to: "/support", label: "Contact Support", Icon: Mail },
      { onClick: openCookiePreferences, label: "Cookie Preferences", Icon: Cookie },
    ],
  },
];


function FooterLinkRow({ link }: { link: FooterLink }) {
  const baseClass = `group flex min-h-[48px] items-center gap-3 rounded-2xl border border-transparent px-3 py-2.5 transition-colors hover:border-gold/30 hover:bg-cream/70 ${
    link.emphasis ? "text-[var(--gold-deep)] font-semibold" : "text-foreground/85"
  }`;

  const inner = (
    <>
      <span className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-gold-light/50 text-[var(--gold-deep)] group-hover:bg-[var(--gradient-gold)] group-hover:text-foreground group-hover:shadow-[var(--shadow-gold)] transition-all">
        <link.Icon className="h-4 w-4" strokeWidth={2.25} />
      </span>
      <span className="min-w-0 truncate text-sm font-medium">{link.label}</span>
    </>
  );

  if (link.to) {
    return (
      <Link to={link.to} className={baseClass}>
        {inner}
      </Link>
    );
  }
  if (link.href) {
    return (
      <a href={link.href} className={baseClass}>
        {inner}
      </a>
    );
  }
  return (
    <button type="button" onClick={link.onClick} className={`${baseClass} w-full text-start`}>
      {inner}
    </button>
  );
}

export function FooterDisclaimer() {
  const [isAdmin, setIsAdmin] = useState(false);

  useEffect(() => {
    let cancelled = false;
    async function check(uid: string | null) {
      if (!uid) {
        if (!cancelled) setIsAdmin(false);
        return;
      }
      const { data, error } = await supabase.rpc("has_role", {
        _user_id: uid,
        _role: "admin",
      });
      if (!cancelled) setIsAdmin(!error && !!data);
    }
    check(readStoredAuthSession()?.user?.id ?? null);
    const handler = (e: Event) => {
      const session = (e as CustomEvent).detail?.session;
      check(session?.user?.id ?? null);
    };
    window.addEventListener("rt-auth-change", handler);
    return () => {
      cancelled = true;
      window.removeEventListener("rt-auth-change", handler);
    };
  }, []);

  return (
    <footer
      className="mt-6 border-t border-gold/15 bg-gradient-to-b from-[var(--sand,#F7F3EA)]/30 to-[var(--sand,#F7F3EA)]/70 pb-[140px] lg:pb-14"
      style={{ paddingBottom: "max(140px, calc(140px + env(safe-area-inset-bottom)))" }}
    >

      <div className="mx-auto max-w-3xl px-5 py-12 text-sm leading-relaxed text-muted-foreground sm:px-6 lg:pb-14">
        {/* Brand block */}
        <div className="text-center md:text-start">
          <Link to="/" className="inline-flex items-center gap-3">
            <img loading="lazy" decoding="async"
              src={logo}
              alt="Recovery Trauma™"
              className="h-11 w-11 rounded-full object-cover ring-1 ring-gold/40"
            />
            <span className="font-display text-lg text-foreground">
              Recovery Trauma<span className="text-[var(--gold-deep)]">™</span>
            </span>
          </Link>
          <p className="mt-4 text-sm leading-relaxed text-foreground/75">
            Healing Beyond Survival Mode<span className="text-[var(--gold-deep)]">™</span>
          </p>
          <p className="mt-2 text-xs text-muted-foreground">Created by Jana Burkhardt</p>
          <p className="mt-1 text-xs text-muted-foreground">
            Recovery Trauma™ Ltd · Company No. 16340718
          </p>

          <div className="mt-7 flex justify-center md:justify-start">
            <ul className="inline-flex flex-wrap items-center justify-center gap-4 sm:gap-5 rounded-full border border-gold/30 bg-cream/70 px-5 py-3 shadow-[var(--shadow-soft)] backdrop-blur">
              {SOCIALS.map(({ name, href, Icon }) => (
                <li key={name}>
                  <a
                    href={href}
                    onClick={(e) => openExternal(href, e)}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={name}
                    title={name}
                    className="inline-flex h-14 w-14 min-h-[44px] min-w-[44px] items-center justify-center rounded-full border border-gold/40 bg-background text-[var(--gold-deep)] shadow-[var(--shadow-soft)] transition-all hover:-translate-y-0.5 hover:bg-gold/15 hover:shadow-[var(--shadow-gold)]"
                  >
                    <Icon className="h-6 w-6" />
                  </a>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Safety quick-links — always visible, large tap targets on mobile */}
        <nav aria-label="Safety pathways" className="mt-8">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <Link
              to="/crisis"
              className="group flex min-h-[56px] items-center gap-3 rounded-2xl border border-[var(--gold-deep)]/50 bg-cream/80 px-4 py-3 shadow-[var(--shadow-soft)] ring-1 ring-[var(--gold-deep)]/20 transition-all hover:-translate-y-0.5 hover:shadow-[var(--shadow-gold)]"
            >
              <span className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-[var(--gradient-gold)] text-foreground shadow-[var(--shadow-gold)]">
                <LifeBuoy className="h-5 w-5" strokeWidth={2.25} />
              </span>
              <span className="min-w-0 flex-1">
                <span className="block truncate font-display text-sm font-semibold text-foreground">Crisis Support</span>
                <span className="block truncate text-xs text-muted-foreground">Immediate help & hotlines</span>
              </span>
            </Link>
            <Link
              to="/escalation-pathway"
              className="group flex min-h-[56px] items-center gap-3 rounded-2xl border border-gold/40 bg-cream/70 px-4 py-3 shadow-[var(--shadow-soft)] transition-all hover:-translate-y-0.5 hover:border-gold/60 hover:shadow-[var(--shadow-gold)]"
            >
              <span className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-gold-light/60 text-[var(--gold-deep)]">
                <ShieldAlert className="h-5 w-5" strokeWidth={2.25} />
              </span>
              <span className="min-w-0 flex-1">
                <span className="block truncate font-display text-sm font-semibold text-foreground">Escalation Pathway</span>
                <span className="block truncate text-xs text-muted-foreground">How concerns are handled</span>
              </span>
            </Link>
          </div>
        </nav>

        {/* Accordion sections — collapsed by default, calm and clutter-free */}
        <div className="mt-6">

          <Accordion type="multiple" className="flex flex-col gap-3">
            {SECTIONS.map((section) => {
              const isCrisis = section.key === "crisis";
              return (
                <AccordionItem
                  key={section.key}
                  value={section.key}
                  className={`overflow-hidden rounded-[1.5rem] border bg-gradient-to-b from-cream/60 via-card to-cream/30 shadow-[var(--shadow-soft)] ${
                    isCrisis
                      ? "border-[var(--gold-deep)]/50 ring-1 ring-[var(--gold-deep)]/20"
                      : "border-gold/25"
                  }`}
                >
                  <AccordionTrigger
                    className="group min-h-[56px] px-5 py-4 hover:no-underline [&>svg]:hidden"
                  >
                    <span className="flex flex-1 items-center gap-3">
                      <span className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-[var(--gradient-gold)] text-foreground shadow-[var(--shadow-gold)]">
                        <section.Icon className="h-5 w-5" strokeWidth={2.25} />
                      </span>
                      <span className="font-display text-base font-medium tracking-wide text-foreground">
                        {section.title}
                      </span>
                    </span>
                    <span
                      aria-hidden
                      className="inline-flex h-10 w-10 sm:h-8 sm:w-8 items-center justify-center rounded-full border border-gold/40 bg-cream/70 text-[var(--gold-deep)] transition-transform duration-300 group-data-[state=open]:rotate-45"
                    >
                      <Plus className="h-4 w-4" strokeWidth={2.5} />
                    </span>
                  </AccordionTrigger>
                  <AccordionContent className="px-3 pb-3 pt-0">
                    <ul className="flex flex-col gap-1">
                      {section.links.map((l) => (
                        <li key={l.label}>
                          <FooterLinkRow link={l} />
                        </li>
                      ))}
                    </ul>
                  </AccordionContent>
                </AccordionItem>
              );
            })}
          </Accordion>
        </div>

        <div
          aria-hidden
          className="mx-auto my-10 h-px w-full bg-gradient-to-r from-transparent via-gold/40 to-transparent"
        />

        <p className="mx-auto max-w-3xl text-center text-xs leading-relaxed">
          Recovery Trauma™ provides educational resources, self-help tools,
          peer support, and wellbeing content. Recovery Trauma™{" "}
          <span className="font-semibold text-foreground">
            is not therapy, medical treatment, or a crisis service
          </span>
          , and is not a substitute for professional healthcare, counselling,
          or emergency support. If you are in immediate danger or require
          urgent help, contact local emergency services or an appropriate
          crisis line.
        </p>

        <div className="mt-6 flex flex-wrap items-center justify-center gap-2">
          <Link
            to="/support"
            className="inline-flex min-h-10 sm:min-h-0 items-center gap-1.5 rounded-full border border-gold/30 bg-cream/70 px-3 py-1 text-[11px] font-medium text-foreground/80 hover:border-gold/50"
          >
            <Mail className="h-3 w-3 text-[var(--gold-deep)]" />
            Contact Support
          </Link>
          <span className="inline-flex min-h-10 sm:min-h-0 items-center gap-1.5 rounded-full border border-gold/30 bg-cream/70 px-3 py-1 text-[11px] font-medium text-foreground/80">
            <Heart className="h-3 w-3 text-[var(--gold-deep)]" />
            Developed by Founder Jana Burkhardt, Recovery Trauma™
          </span>
        </div>


        {isAdmin && (
          <div className="mt-6 flex justify-center">
            <Link
              to="/admin"
              className="inline-flex items-center gap-2 rounded-full border border-gold/40 bg-cream/80 px-4 py-2 text-xs font-semibold text-[var(--gold-deep)] shadow-[var(--shadow-soft)] hover:bg-gold/15 hover:shadow-[var(--shadow-gold)] transition-all"
            >
              <ShieldCheck className="h-4 w-4" />
              Admin Dashboard
            </Link>
          </div>
        )}

        <p className="mt-5 text-center text-[11px] uppercase tracking-[0.18em] text-muted-foreground/80">
          © {new Date().getFullYear()} Recovery Trauma™ Ltd · Created by Jana Burkhardt · All rights reserved
        </p>

      </div>
    </footer>
  );
}
